import React, { useEffect, useState } from 'react';
import { WarningCircle, CheckCircle, X } from 'phosphor-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '@/store';
import { useMemberAccess } from '@/hooks/useMemberAccess';
import { MemberStatus } from '@/types/member.types';
import { UiBlock, V2MembershipStatusBanner } from '@/v2/routes';
import './MembershipStatusBanner.css';

const SESSION_DISMISS_KEY = 'membershipBannerDismissedStatus';

type BannerVariant = 'warning' | 'error' | 'success';

interface BannerConfig {
  message: string;
  variant: BannerVariant;
}

export const MembershipStatusBanner: React.FC = () => {
  const { t } = useTranslation();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const { status, wasReactivated, clearReactivationFlag, paymentFailure } = useMemberAccess();
  const [dismissed, setDismissed] = useState(false);

  // Dismissal is keyed on the phase (not the status) while a payment-failure
  // sequence is showing — so a member who dismisses the day 0-7 warning and
  // is later suspended still sees the suspension surface, and one who
  // dismisses day 8-14's suspension still sees day 15+'s ended surface even
  // though `status` can stay MemberStatus.SUSPENDED across both (only
  // subscriptionStatus flips at day 15) — without this, the stale
  // dismiss-key would match and the new message would never show.
  const dismissKey =
    paymentFailure.phase === 'grace' ||
    paymentFailure.phase === 'suspended' ||
    paymentFailure.phase === 'ended'
      ? paymentFailure.phase
      : status;

  // Reset dismissed state when status changes or on mount
  useEffect(() => {
    if (wasReactivated) {
      setDismissed(false);
      return;
    }
    const dismissedStatus = sessionStorage.getItem(SESSION_DISMISS_KEY);
    setDismissed(dismissedStatus === dismissKey);
  }, [dismissKey, wasReactivated]);

  if (!isAuthenticated || dismissed) return null;

  if (wasReactivated) {
    const dismissReactivation = () => {
      clearReactivationFlag();
      setDismissed(true);
    };

    return (
      <UiBlock
        v1={
          <div className="msb msb--success" role="status">
            <CheckCircle size={20} weight="fill" className="msb__icon" />
            <p className="msb__message">{t('membership.banner.reactivated')}</p>
            <button
              className="msb__close"
              onClick={dismissReactivation}
              aria-label="Dismiss"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        }
        v2={
          <V2MembershipStatusBanner
            variant="success"
            message={t('membership.banner.reactivated')}
            onDismiss={dismissReactivation}
          />
        }
      />
    );
  }

  // The countdown warning takes priority over bannerConfigs[status] — a
  // grace-phase member may also carry status: payment_failed.
  if (paymentFailure.phase === 'grace') {
    const message = t('membership.banner.paymentFailedGrace', {
      count: paymentFailure.daysRemaining,
    });

    const handleDismiss = () => {
      sessionStorage.setItem(SESSION_DISMISS_KEY, 'grace');
      setDismissed(true);
    };

    return (
      <UiBlock
        v1={
          <div className="msb msb--warning" role="alert">
            <WarningCircle size={20} weight="fill" className="msb__icon" />
            <p className="msb__message">{message}</p>
            <button className="msb__close" onClick={handleDismiss} aria-label="Dismiss">
              <X size={16} weight="bold" />
            </button>
          </div>
        }
        v2={
          <V2MembershipStatusBanner
            variant="warning"
            message={message}
            onDismiss={handleDismiss}
          />
        }
      />
    );
  }

  // Day 8+ (suspended) and day 15+ (ended) also take priority over
  // bannerConfigs[status] — MemberAccessGuard blocks the guarded routes for
  // these phases, but Home itself is never guarded (D-0..7's "app opens
  // normally with a banner" requirement extends through the later phases
  // too), so the member must still see why they're blocked from here.
  // Same copy as MemberAccessGuard's block wall — one message per phase.
  if (paymentFailure.phase === 'suspended' || paymentFailure.phase === 'ended') {
    const message =
      paymentFailure.phase === 'ended'
        ? t('membership.accessBlocked.ended')
        : t('membership.accessBlocked.suspended');

    const handleDismiss = () => {
      sessionStorage.setItem(SESSION_DISMISS_KEY, paymentFailure.phase);
      setDismissed(true);
    };

    return (
      <UiBlock
        v1={
          <div className="msb msb--error" role="alert">
            <WarningCircle size={20} weight="fill" className="msb__icon" />
            <p className="msb__message">{message}</p>
            <button className="msb__close" onClick={handleDismiss} aria-label="Dismiss">
              <X size={16} weight="bold" />
            </button>
          </div>
        }
        v2={
          <V2MembershipStatusBanner
            variant="error"
            message={message}
            onDismiss={handleDismiss}
          />
        }
      />
    );
  }

  const bannerConfigs: Partial<Record<MemberStatus, BannerConfig>> = {
    [MemberStatus.CANCELLED]: {
      message: t('membership.banner.cancelled'),
      variant: 'warning',
    },
    [MemberStatus.PAUSED]: {
      message: t('membership.banner.paused'),
      variant: 'warning',
    },
    [MemberStatus.PAYMENT_FAILED]: {
      message: t('membership.banner.paymentFailed'),
      variant: 'error',
    },
    [MemberStatus.PAST_DUE]: {
      message: t('membership.banner.pastDue'),
      variant: 'error',
    },
  };

  const config = status ? bannerConfigs[status] : undefined;
  if (!config) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, status!);
    setDismissed(true);
  };

  /* One set of rules about when a member is told their membership
     is paused — the status, the session dismissal and the
     reactivation flag all stay here. Only the surface differs
     between the two trees. */
  return (
    <UiBlock
      v1={
        <div className={`msb msb--${config.variant}`} role="alert">
          <WarningCircle size={20} weight="fill" className="msb__icon" />
          <p className="msb__message">{config.message}</p>
          <button className="msb__close" onClick={handleDismiss} aria-label="Dismiss">
            <X size={16} weight="bold" />
          </button>
        </div>
      }
      v2={
        <V2MembershipStatusBanner
          variant={config.variant}
          message={config.message}
          onDismiss={handleDismiss}
        />
      }
    />
  );
};
